import * as XLSX from 'xlsx'
import { format } from 'date-fns'
import { User } from '../models/User'
import { Survey, SURVEY_TYPE_LABELS, SURVEY_DIMENSION_LABELS } from '../models/Survey'
import { ScreenLog, UsageStats } from '../stores/userStore'
import { getScreenLabel } from './screenNames'
import { formatDuration } from './formatDuration'

interface ScreenStats {
  totalViews: number
  totalDurationSeconds: number
}

// 欄位名稱映射（使用者稍後可自訂）
const PAGE_VIEW_HEADERS: Record<string, string> = {
  screenName: '頁面名稱',
  totalViews: '總瀏覽量',
  totalDurationFormatted: '總瀏覽時間',
  averageDurationFormatted: '平均瀏覽時間',
}

export function exportPageViewStats(
  stats: Record<string, ScreenStats>,
  fileName: string = '頁面瀏覽統計.xlsx'
) {
  const rows = Object.entries(stats).map(([screen, data]) => {
    const avgSeconds =
      data.totalViews > 0
        ? Math.round(data.totalDurationSeconds / data.totalViews)
        : 0
    return {
      [PAGE_VIEW_HEADERS.screenName]: screen,
      [PAGE_VIEW_HEADERS.totalViews]: data.totalViews,
      [PAGE_VIEW_HEADERS.totalDurationFormatted]: formatDuration(
        data.totalDurationSeconds
      ),
      [PAGE_VIEW_HEADERS.averageDurationFormatted]: formatDuration(avgSeconds),
    }
  })

  // 依總瀏覽量降序
  rows.sort(
    (a, b) =>
      (b[PAGE_VIEW_HEADERS.totalViews] as number) -
      (a[PAGE_VIEW_HEADERS.totalViews] as number)
  )

  const ws = XLSX.utils.json_to_sheet(rows)
  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, '頁面瀏覽統計')
  XLSX.writeFile(wb, fileName)
}

interface SessionRecord {
  userId: string
  userName?: string
  startedAt: Date
  endedAt: Date | null
  durationSeconds: number
  isActive: boolean
}

export function exportSessionStats(
  sessions: SessionRecord[],
  fileName: string = 'Session統計.xlsx'
) {
  const rows = sessions.map((s) => ({
    使用者: s.userName || s.userId,
    開始時間: s.startedAt.toLocaleString('zh-TW'),
    結束時間: s.endedAt ? s.endedAt.toLocaleString('zh-TW') : '進行中',
    時長: formatDuration(s.durationSeconds),
    狀態: s.isActive ? '進行中' : '已結束',
  }))

  const ws = XLSX.utils.json_to_sheet(rows)
  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, 'Session紀錄')
  XLSX.writeFile(wb, fileName)
}

// ==================== 個人報告匯出 ====================

const GENDER_LABELS: Record<string, string> = {
  male: '男',
  female: '女',
  other: '其他',
}

const EDUCATION_LABELS: Record<string, string> = {
  bachelor: '學士',
  master: '碩士',
  phd: '博士',
}

function formatDateTime(d: Date | undefined | null): string {
  if (!d) return '-'
  return format(d, 'yyyy/MM/dd HH:mm:ss')
}

function formatDateTimeShort(d: Date | undefined | null): string {
  if (!d) return '-'
  return format(d, 'yyyy/MM/dd HH:mm')
}

/**
 * 匯出單一使用者完整報告（多 sheet Excel）。
 * Sheet 1 基本資訊 / Sheet 2 頁面停留時間 / Sheet 3 使用紀錄 / Sheet 4 量表結果
 */
export function exportUserReport(
  user: User,
  screenLogs: ScreenLog[],
  usageStats: UsageStats | null,
  userSurveys: Survey[]
): void {
  const wb = XLSX.utils.book_new()

  // ----- Sheet 1: 基本資訊 -----
  const basicRows = [
    { 項目: '姓名', 內容: user.displayName },
    { 項目: 'Email', 內容: user.email || '-' },
    { 項目: '性別', 內容: GENDER_LABELS[user.gender] ?? user.gender },
    { 項目: '學歷', 內容: EDUCATION_LABELS[user.education] ?? user.education },
    { 項目: '年級', 內容: `${user.grade} 年級` },
    { 項目: '註冊時間', 內容: formatDateTimeShort(user.registeredAt) },
    {
      項目: '註冊方式',
      內容: user.registeredBy === 'qr_code' ? 'QR Code' : 'Email',
    },
    { 項目: '總使用時間', 內容: formatDuration(user.totalScreenTime) },
    { 項目: '登入次數', 內容: `${user.loginCount} 次` },
    { 項目: '最後登入', 內容: formatDateTimeShort(user.lastLoginAt) },
  ]
  const wsBasic = XLSX.utils.json_to_sheet(basicRows)
  XLSX.utils.book_append_sheet(wb, wsBasic, '基本資訊')

  // ----- Sheet 2: 頁面停留時間 -----
  const pageEntries = Object.entries(usageStats?.screenTimeByPage ?? {})
  const pageRows = pageEntries
    .map(([name, seconds]) => ({
      頁面: getScreenLabel(name),
      停留時間: formatDuration(seconds),
      秒數: seconds,
    }))
    .sort((a, b) => b.秒數 - a.秒數)
    .map(({ 頁面, 停留時間 }) => ({ 頁面, 停留時間 }))
  const wsPages = XLSX.utils.json_to_sheet(
    pageRows.length > 0 ? pageRows : [{ 頁面: '-', 停留時間: '-' }]
  )
  XLSX.utils.book_append_sheet(wb, wsPages, '頁面停留時間')

  // ----- Sheet 3: 使用紀錄 -----
  const logRows = screenLogs.map((log) => ({
    頁面: getScreenLabel(log.screenName),
    停留時間: formatDuration(log.duration),
    進入時間: formatDateTime(log.enteredAt),
    離開時間: formatDateTime(log.exitedAt),
    'Session ID': log.sessionId,
  }))
  const wsLogs = XLSX.utils.json_to_sheet(
    logRows.length > 0
      ? logRows
      : [
          {
            頁面: '-',
            停留時間: '-',
            進入時間: '-',
            離開時間: '-',
            'Session ID': '-',
          },
        ]
  )
  XLSX.utils.book_append_sheet(wb, wsLogs, '使用紀錄')

  // ----- Sheet 4: 量表結果 -----
  // 預先展開所有量表類型的構面欄位，保持固定欄位順序
  const allDimHeaders: Array<{ key: string; surveyType: string; header: string }> = []
  for (const [sType, dims] of Object.entries(SURVEY_DIMENSION_LABELS)) {
    Object.keys(dims).forEach((key, i) => {
      allDimHeaders.push({ key, surveyType: sType, header: `${dims[key]}（${i + 1}）` })
    })
  }

  const surveyRows = userSurveys.map((s) => {
    const dimLabels = SURVEY_DIMENSION_LABELS[s.surveyType] ?? {}
    const dimKeys = Object.keys(dimLabels)
    const dominantIdx = dimKeys.indexOf(s.dominantDimension)
    const dominantLabel = dimLabels[s.dominantDimension] ?? s.dominantDimension
    const dominantWithNum = dominantIdx >= 0 ? `${dominantLabel}（${dominantIdx + 1}）` : dominantLabel

    const isOE = s.surveyType === 'outcome_expectation'
    const oeSum = isOE
      ? Object.values(s.scores).reduce((acc, v) => acc + v, 0)
      : null

    const durationStr = s.durationSeconds != null
      ? `${Math.floor(s.durationSeconds / 60)}:${String(s.durationSeconds % 60).padStart(2, '0')}`
      : ''
    const row: Record<string, string | number> = {
      量表類型: SURVEY_TYPE_LABELS[s.surveyType] ?? s.surveyType,
      完成時間: formatDateTimeShort(s.completedAt),
      填寫時長: durationStr,
      是否重填: s.isRetake != null ? (s.isRetake ? '是' : '否') : '',
      入口來源: s.entrySource ?? '',
      主要特質: dominantWithNum,
      '結果預期總分（/40）': oeSum !== null ? oeSum : '',
      高低結果預期: oeSum !== null ? (oeSum >= 25 ? '高' : '低') : '',
    }
    for (const { key, surveyType, header } of allDimHeaders) {
      row[header] =
        surveyType === s.surveyType && s.scores[key] !== undefined
          ? Number(s.scores[key].toFixed(2))
          : ''
    }
    return row
  })
  const surveyHeader = [
    '量表類型', '完成時間', '填寫時長', '是否重填', '入口來源', '主要特質',
    '結果預期總分（/40）', '高低結果預期',
    ...allDimHeaders.map((h) => h.header),
  ]
  const wsSurveys = XLSX.utils.json_to_sheet(
    surveyRows.length > 0
      ? surveyRows
      : [{ 量表類型: '-', 完成時間: '-', 主要特質: '-' }],
    { header: surveyHeader }
  )
  XLSX.utils.book_append_sheet(wb, wsSurveys, '量表結果')

  const safeName = (user.displayName || user.id).replace(/[\\/:*?"<>|]/g, '_')
  const dateStr = format(new Date(), 'yyyyMMdd')
  XLSX.writeFile(wb, `${safeName}_個人報告_${dateStr}.xlsx`)
}
